import { Action, Alert, NumberInput } from 'corso-admin-ui';
import { CrewClaimResolutionMethodEnum } from 'corso-types';
import { useState } from 'react';
import { SupportedClaimLineItem } from '~/features/claimReview/ClaimReviewProvider';
import { useClaimOrderLineage } from '~/features/claimReview/hooks/useClaimOrderLineage';
import { useCurrency } from '~/hooks/useCurrency';
import ExternalResolutionModifier from './ExternalResolutionModifier';
import { ResolutionModifierProps } from './types';

// the claim line's own values: on an exchange of an exchange, what was paid on the originating order
function claimLineItemTotalPrice(claimLineItem: SupportedClaimLineItem) {
  const { unitPrice, unitTax } = claimLineItem;
  return Number(((unitPrice + unitTax) * claimLineItem.quantity).toFixed(2));
}

type MonetaryResolutionMethod =
  | typeof CrewClaimResolutionMethodEnum.refund
  | typeof CrewClaimResolutionMethodEnum.giftCard;

export default function MonetaryModifier({
  resolutionMethod,
  claimLineItem,
  onCancel,
  onSave,
}: ResolutionModifierProps<MonetaryResolutionMethod> & {
  resolutionMethod: MonetaryResolutionMethod;
}) {
  const totalAmount = claimLineItemTotalPrice(claimLineItem);
  const { currencyCode, currencySymbol } = useCurrency();
  const { data: lineage } = useClaimOrderLineage();

  // a line lineage could not trace has no paid value to compare against
  const lineTrace = lineage?.lineTraces.find(
    (trace) => trace.lineItemId === claimLineItem.id,
  );
  const isUntraced = !!lineTrace && lineTrace.matched !== 'exact';

  const [approvedAmount, setApprovedAmount] = useState(
    claimLineItem.amount ?? totalAmount,
  );

  const [wasResolvedExternally, setWasResolvedExternally] = useState(
    !!claimLineItem.wasResolvedExternally,
  );

  return (
    <>
      {isUntraced && (
        <Alert variant="info" heading="Amount Not Traced">
          This item could not be traced to the order the customer paid on. Enter
          what they paid; the claim lineage shows the original order.
        </Alert>
      )}
      {!isUntraced &&
        approvedAmount > totalAmount && (
          // TODO have this warning for gift cards, and prevent approving more than requested for refunds to prevent errors
          <Alert variant="warning" heading="Approving More Than Original Value">
            {resolutionMethod !== CrewClaimResolutionMethodEnum.giftCard &&
              'This may cause the refund to fail.'}
          </Alert>
        )}
      <ExternalResolutionModifier
        onChange={setWasResolvedExternally}
        checked={wasResolvedExternally}
      />
      <NumberInput
        min={0}
        step={0.01}
        id="monetary-resolution-amount"
        label="Amount"
        addon={{
          insideStart: currencySymbol,
          insideEnd: currencyCode,
        }}
        value={approvedAmount}
        onChange={(event) => setApprovedAmount(event.target.valueAsNumber)}
      />
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
        <Action variant="ghost" onClick={onCancel}>
          Cancel
        </Action>
        <Action
          variant="primary"
          onClick={() => {
            onSave({
              resolutionMethod,
              amount: approvedAmount,
              wasResolvedExternally,
            });
          }}
        >
          Save
        </Action>
      </div>
    </>
  );
}
